import { useEffect, useRef, useState } from "react";

export interface ViewerImage {
  url: string;
  alt: string;
}

/** Native modal dialogs keep keyboard focus inside the viewer and background inert. */
export function ImageViewer({
  images,
  initialIndex,
  onClose,
}: {
  images: ViewerImage[];
  initialIndex: number;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const viewport = useRef<HTMLDivElement>(null);
  const image = images[index];

  useEffect(() => {
    const element = dialog.current!;
    const trigger = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element.showModal();
    closeButton.current?.focus();
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      trigger?.focus();
    };
  }, []);

  useEffect(() => {
    if (viewport.current) {
      viewport.current.scrollTop = 0;
      viewport.current.scrollLeft = 0;
    }
  }, [index, zoom]);

  const changeImage = (nextIndex: number) => {
    setIndex(nextIndex);
    setZoom(1);
  };

  const controlClass =
    "rounded-lg px-3 py-2 text-sm font-medium hover:bg-f4f7ff focus-visible:outline focus-visible:outline-2 focus-visible:outline-3461fd disabled:opacity-40";

  return (
    <dialog
      ref={dialog}
      aria-label="Instruction image viewer"
      className="w-[calc(100%-2rem)] max-w-5xl max-h-[90vh] rounded-2xl p-0 shadow-xl backdrop:bg-black/60"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" && index > 0) {
          event.preventDefault();
          changeImage(index - 1);
        } else if (event.key === "ArrowRight" && index < images.length - 1) {
          event.preventDefault();
          changeImage(index + 1);
        }
      }}
    >
      <div className="flex max-h-[90vh] flex-col bg-white p-3 sm:p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1" aria-label="Image zoom">
            <button
              type="button"
              className={controlClass}
              disabled={zoom === 1}
              aria-label="Zoom out"
              onClick={() => setZoom(zoom - 1)}
            >
              −
            </button>
            <span className="text-sm" aria-live="polite">
              {zoom * 100}%
            </span>
            <button
              type="button"
              className={controlClass}
              disabled={zoom === 3}
              aria-label="Zoom in"
              onClick={() => setZoom(zoom + 1)}
            >
              +
            </button>
          </div>
          <button
            ref={closeButton}
            type="button"
            className={controlClass}
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div ref={viewport} className="min-h-0 overflow-auto rounded-lg">
          <img
            src={image.url}
            alt={image.alt}
            className="mx-auto h-auto object-contain"
            style={{
              width: zoom === 1 ? undefined : `${zoom * 100}%`,
              maxWidth: zoom === 1 ? "100%" : "none",
              maxHeight: zoom === 1 ? "65vh" : "none",
            }}
          />
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            className={controlClass}
            disabled={index === 0}
            onClick={() => changeImage(index - 1)}
          >
            Previous
          </button>
          <p className="text-center text-sm" aria-live="polite">
            {index + 1} / {images.length}
          </p>
          <button
            type="button"
            className={controlClass}
            disabled={index === images.length - 1}
            onClick={() => changeImage(index + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </dialog>
  );
}
